/**
 * Session state for the active web client.
 *
 * Lifetime rules (intentional — read before changing):
 *
 * - **Access token lives in memory only.** Never localStorage, never a JS
 *   cookie. Lost on tab reload — that's fine, we restore via the silent
 *   refresh from the HttpOnly `ezjwt_rt` cookie set by the server. XSS
 *   that reads `localStorage` cannot exfiltrate our access token.
 *
 * - **Refresh token is never touched by JS.** The browser ships it
 *   automatically as the `ezjwt_rt` cookie on `/wp-json/ez-jwt/v1/auth/*`
 *   requests (Path-restricted, HttpOnly, Secure, SameSite=None on
 *   cross-host deploys).
 *
 * - **CSRF token** travels in `localStorage` and in memory. The server
 *   mirrors it into the `X-EZJWT-CSRF` response header on every login
 *   and refresh; we read it from there and persist so F5 can rebuild it
 *   without a network roundtrip. The Set-Cookie variant exists too but
 *   isn't reliably JS-readable when the SPA and API sit on different
 *   hosts (host-only cookies don't cross subdomain/eTLD boundaries to
 *   `document.cookie` even when the browser sends them back to the API
 *   correctly). XSS reading localStorage CAN exfiltrate this token, but
 *   it's only useful when paired with the HttpOnly refresh cookie — an
 *   XSS that's already in your page can issue authenticated requests
 *   directly without needing CSRF at all, so the exposure isn't a
 *   meaningful regression.
 *
 * - **User / sites** are kept in memory too. They're cheap to refetch on
 *   bootstrap; persisting them would just create a stale-vs-current
 *   reconciliation problem.
 */
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { Site } from '@/entities/site'
import type { User } from '@/entities/user'
import { env } from '@/app/config/env'
import { scopedStorage } from '@/shared/lib/scoped-storage'

// API host + CSRF are backend-environment state, so they're stored
// env-scoped (`ezqr:<env>:*`) — a leftover record from a different build
// served on the same origin can't leak into this one. See
// scoped-storage.ts for the why.
/** Scoped-storage key for the persisted API host (non-sensitive — a URL). */
const API_HOST_KEY = 'api-host'
/** Scoped-storage key for the persisted CSRF token. */
const CSRF_KEY = 'csrf'

function readPersistedCsrf(): string | null {
  const raw = scopedStorage.get(CSRF_KEY)
  return raw && raw.length > 0 ? raw : null
}

function writePersistedCsrf(csrf: string | null): void {
  scopedStorage.set(CSRF_KEY, csrf)
}

/**
 * Restore the persisted API host. Two guards, defence in depth:
 *  1. The record is env-scoped (`scopedStorage`), so a different build
 *     on the same origin (e.g. a prod build briefly deployed to a dev
 *     host) can't have its host leak into this build — the scoped key
 *     simply isn't found and we fall back to the env default.
 *  2. Even within this env, only accept a host we actually ship
 *     (`env.prodApiHosts`). The host becomes the apiClient's `baseURL`,
 *     which carries the Bearer token; a tampered value must never be
 *     able to redirect that token to an arbitrary origin.
 * Anything failing either check falls back to the env default.
 */
function readPersistedApiHost(): string | null {
  const raw = scopedStorage.get(API_HOST_KEY)
  if (!raw) return null
  return env.prodApiHosts.includes(raw) ? raw : null
}

function writePersistedApiHost(host: string | null): void {
  scopedStorage.set(API_HOST_KEY, host)
}

export const useSessionStore = defineStore('session', () => {
  /** Current access token (RS256 JWT) or null when unauthenticated. */
  const accessToken = ref<string | null>(null)
  /** Absolute expiry of the access token, UNIX milliseconds. */
  const expiresAt = ref<number | null>(null)
  /** Profile of the currently signed-in user. */
  const user = ref<User | null>(null)
  /** Sites this user can manage on the active API host. */
  const sites = ref<Site[]>([])
  /**
   * CSRF token mirrored from the `X-EZJWT-CSRF` response header on
   * login/refresh. Hydrated from localStorage so it survives F5.
   */
  const csrfToken = ref<string | null>(readPersistedCsrf())
  /**
   * The API host this session was opened against. Persisted in
   * localStorage (allow-listed on restore) so the prod domain switcher
   * remembers the last choice across reloads — without it, a user on a
   * non-default network reloads onto the wrong `baseURL`, the silent
   * refresh hits the wrong host, and they're logged out.
   */
  const apiHost = ref<string | null>(readPersistedApiHost())

  const isAuthenticated = computed(() => accessToken.value !== null && user.value !== null)

  /**
   * Returns true if the access token will expire within `skewMs` ms.
   * Used by the API client to refresh slightly before expiry.
   */
  function isExpiring(skewMs = 60_000): boolean {
    if (expiresAt.value === null) return true
    return Date.now() + skewMs >= expiresAt.value
  }

  /**
   * Apply the tokens + CSRF returned by `/auth/login` or `/auth/refresh`.
   * CSRF rotates every refresh — callers MUST pass the new value
   * (read from the `X-EZJWT-CSRF` response header) so the next refresh
   * doesn't 403 with `csrf_mismatch`. Persisted to localStorage so F5
   * can rebuild it.
   */
  function setTokens(token: string, expiresInSec: number, csrf: string | null): void {
    accessToken.value = token
    expiresAt.value = Date.now() + expiresInSec * 1000
    if (csrf !== null) {
      csrfToken.value = csrf
      writePersistedCsrf(csrf)
    }
  }

  function setProfile(profile: { user: User; sites: Site[] }): void {
    user.value = profile.user
    sites.value = profile.sites
  }

  function setApiHost(host: string): void {
    apiHost.value = host
    writePersistedApiHost(host)
  }

  /** Drop everything. Caller is responsible for navigation. */
  function clear(): void {
    accessToken.value = null
    expiresAt.value = null
    user.value = null
    sites.value = []
    csrfToken.value = null
    writePersistedCsrf(null)
    // Don't clear apiHost — we want to remember the user's choice for next login.
  }

  return {
    // state
    accessToken,
    expiresAt,
    user,
    sites,
    csrfToken,
    apiHost,
    // getters
    isAuthenticated,
    // actions
    isExpiring,
    setTokens,
    setProfile,
    setApiHost,
    clear,
  }
})
